import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';
interface BarChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  barColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-bar' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function BarChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  barColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: BarChartVisualProps) {
  // In support mode, we might want to force showing values to help explain the answer
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-bar');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-12526" data-mp="2ywlgjco0cjd">
        <span className="font-semibold text-slate-700" data-id="element-12527" data-mp="3k7jlx8xe1bkm">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-12528" data-mp="3p6wbi1fn8wqw">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-12529" data-mp="grclrce6lwgl">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-12530" data-mp="6zqd9nt8abhj">
        <caption data-id="element-12531" data-mp="6ynlzerzu713">
          Staafdiagram data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-12532" data-mp="znm44bw4996e">
          <tr data-id="element-12533" data-mp="zn2ncyi7cqty">
            <th scope="col" data-id="element-12534" data-mp="zmjgccu3bmup">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-12535" data-mp="14eug9fotz8n9">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-12536" data-mp="1eab6qmlo02h0">
          {data.map((item, i) => <tr key={i} data-id="element-12537" data-mp="1e9rwls29aidj" data-mpi={i}>
              <td data-id="element-12538" data-mp="19bgn5ifvi9vu">{item.label}</td>
              <td data-id="element-12539" data-mp="19b8a9qjr34kn">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-12540" data-mp="159d68eafqd36">
        <ResponsiveContainer width="100%" height="100%" data-id="element-12541" data-mp="10a0bl7dbo1q1">
          <BarChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-12542" data-mp="vbp4h4eew5bi">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-12543" data-mp="llvrbyymfi55" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-12544" data-mp="lm5ihpvejvcf" />

            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-12545" data-mp="2x7xxr2ybzkdn" />

            <Tooltip content={<CustomTooltip data-id="element-12547" data-mp="1lsrcvouf7tso" />} cursor={{
            fill: '#f1f5f9'
          }} data-id="element-12546" data-mp="1ltkbu3n51f09" />

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-12548" data-mp="1cf0hq6bod842" data-mpi={i} />)}

            <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-12549" data-mp="1lex6e7y2e6z4">
              {effectiveValueDisplay === 'on-bar' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} data-id="element-12550" data-mp="1ggc6yaqmzrbq" />}
              {data.map((_entry, index) => <Cell key={`cell-${index}`} fill={barColor} data-id="element-12551" data-mp="zbvjh68wd4b" data-mpi={index} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
}